import { t } from 'i18next';
import { useHandlers } from '../../../../context/page-context';
import { useApplyDraftFormValues } from '../../../../utils/form';
import { priceListBuilderToPriceList } from '../../builder-transform';
import { PriceListCsvError, PriceListError, type PriceListUpsertError } from '../../errors';
import { useCreatePriceList } from '../../hooks/use-create-price-list';
import { PriceListFormContext, usePriceListFormLogic } from '../../hooks/use-price-list-form';
import type { PriceListBuilderState, PriceListBuilderValidated } from '../../schemas';
import type { PriceList } from '../../types';
import { PriceListBuilder } from './price-list-builder';

export function PriceListCreateForm({
  initialState,
  draftState,
  onSuccess,
  onCancel,
}: {
  initialState?: PriceListBuilderState;
  draftState?: PriceListBuilderState;
  onSuccess: (priceList: PriceList) => Promise<void> | void;
  onCancel: () => Promise<void> | void;
}) {
  const { notificationHandler } = useHandlers();
  const createPriceList = useCreatePriceList();

  const priceListForm = usePriceListFormLogic({
    initialState,
    onSubmit: handleSubmit,
    onFailValidation: () => {
      notificationHandler.notify(t('notifications:titles.error'), t('errors:generic'), 'error');
    },
    onSubmitError: handleError,
  });

  useApplyDraftFormValues(priceListForm, draftState);

  function handleError(error: PriceListUpsertError) {
    if (error instanceof PriceListError) {
      notificationHandler.notify(
        t('notifications:titles.error'),
        t(`price_list_builder.notifications.errors.${error.id}`),
        'error',
      );
      return;
    }

    if (error instanceof PriceListCsvError) {
      notificationHandler.notify(
        t('notifications:titles.error'),
        t('price_list_builder.notifications.errors.price_list_csv'),
        'error',
      );

      const formattedErrors = error.errors.map((errString) => ({
        message: errString,
      }));

      priceListForm.setFieldMeta('manualPricing.file', (meta) => ({
        ...meta,
        errorMap: {
          ...meta.errorMap,
          onSubmit: formattedErrors,
        },
      }));
      return;
    }

    notificationHandler.notify(
      t('notifications:titles.error'),
      t('price_list_builder.notifications.errors.create_failed'),
      'error',
    );

    throw error;
  }

  async function handleSubmit(data: PriceListBuilderValidated) {
    const payload = priceListBuilderToPriceList(data);

    await createPriceList.mutateAsync(
      { priceList: payload, manualCsv: data.manualPricing?.file?.file },
      {
        onSuccess: async () => {
          notificationHandler.notify(
            t('notifications:titles.success'),
            t('price_list_builder.notifications.success.create', { name: data.name }),
            'success',
          );

          await onSuccess(payload);
        },
      },
    );
  }

  return (
    <PriceListFormContext.Provider value={priceListForm}>
      <PriceListBuilder onCancel={onCancel} />
    </PriceListFormContext.Provider>
  );
}
